/*
 * HomeCrest delivery serviceability checker
 * ===========================================================================
 * <hc-pincode> validates an Indian pincode and asks the configured
 * serviceability endpoint whether HomeCrest delivers there.
 *
 * IT INVENTS NOTHING. There are four ways this ends and only one of them is a
 * delivery answer this file made up - none:
 *
 *   live          a real endpoint is configured; the answer is the endpoint's
 *   fixture       a dev prefix list answers, and the result is stamped FIXTURE
 *   unconfigured  nothing is wired up; it says checks are not available yet
 *   error         the request failed or was malformed; it says so
 *
 * An error is deliberately NOT reported as "not serviceable". Telling a
 * customer their pincode is out of range because a fetch timed out would be
 * fabricating a delivery answer, which is the one thing this must never do.
 *
 * The contract is documented in snippets/homecrest-pincode.liquid.
 */

const PRODUCT_SELECT = 'shopify:product:select';
const PIN = /^[1-9][0-9]{5}$/;

class HcPincode extends HTMLElement {
  #input = null;
  #submit = null;
  #result = null;
  #main = null;
  #eta = null;
  #hint = null;
  #controller = null;
  /** The last pincode that produced a real answer, so a variant change can re-ask. */
  #lastChecked = null;

  connectedCallback() {
    this.#input = this.querySelector('[data-hc-pin-input]');
    this.#submit = this.querySelector('[data-hc-pin-submit]');
    this.#result = this.querySelector('[data-hc-pin-result]');
    this.#main = this.querySelector('[data-hc-pin-main]');
    this.#eta = this.querySelector('[data-hc-pin-eta]');
    this.#hint = this.querySelector('[data-hc-pin-hint]');
    if (!this.#input || !this.#submit || !this.#result) return;

    this.#submit.addEventListener('click', this.#onSubmit);
    this.#input.addEventListener('keydown', this.#onKeydown);
    this.#input.addEventListener('input', this.#onInput);
    document.addEventListener(PRODUCT_SELECT, this.#onVariant);
  }

  disconnectedCallback() {
    this.#submit?.removeEventListener('click', this.#onSubmit);
    this.#input?.removeEventListener('keydown', this.#onKeydown);
    this.#input?.removeEventListener('input', this.#onInput);
    document.removeEventListener(PRODUCT_SELECT, this.#onVariant);
    this.#controller?.abort();
  }

  /* Digits only, so "letters" and pasted junk simply cannot enter the field.
     Typing again clears a stale result rather than leaving the previous
     pincode's answer sitting under a different number. */
  #onInput = () => {
    const cleaned = this.#input.value.replace(/\D/g, '').slice(0, 6);
    if (cleaned !== this.#input.value) this.#input.value = cleaned;
    if (this.#lastChecked && cleaned !== this.#lastChecked) this.#clear();
  };

  #onKeydown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.#onSubmit();
    }
  };

  #onSubmit = () => {
    const value = (this.#input.value || '').trim();

    if (!PIN.test(value)) {
      this.#lastChecked = null;
      this.#render('invalid', this.dataset.msgInvalid);
      this.#input.focus();
      return;
    }

    if (this.dataset.mode === 'unconfigured') {
      this.#lastChecked = null;
      this.#render('unconfigured', this.dataset.msgUnconfigured);
      return;
    }

    this.#check(value);
  };

  /* A variant change can change the delivery answer, so a result already on
     screen is re-asked rather than left to go stale. */
  #onVariant = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    if (!this.#lastChecked) return;
    this.#check(this.#lastChecked);
  };

  async #check(pincode) {
    this.#controller?.abort();
    this.#controller = new AbortController();

    this.#render('checking', this.dataset.msgChecking);
    this.#submit.disabled = true;

    try {
      const answer =
        this.dataset.mode === 'fixture'
          ? this.#fixtureAnswer(pincode)
          : await this.#fetchAnswer(pincode, this.#controller.signal);

      this.#lastChecked = pincode;

      if (answer.serviceable) {
        /* Two parts, not one string: the availability and the estimate are
           separate spans so they sit on one line when there is room and the
           estimate drops to a second line when there is not. Nothing forces
           either outcome. */
        this.#render(
          'yes',
          this.#fill(this.dataset.msgYes, pincode),
          answer.fixture,
          answer.eta ? `${this.dataset.etaLabel} ${answer.eta}` : ''
        );
      } else {
        this.#render('no', answer.message || this.#fill(this.dataset.msgNo, pincode), answer.fixture);
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      // An error is not an answer. The customer is told the check failed, not
      // that HomeCrest does not deliver to them.
      this.#lastChecked = null;
      this.#render('error', this.dataset.msgError);
    } finally {
      this.#submit.disabled = false;
      this.#controller = null;
    }
  }

  async #fetchAnswer(pincode, signal) {
    const url = new URL(this.dataset.endpoint, window.location.origin);
    url.searchParams.set('pincode', pincode);
    if (this.dataset.productHandle) url.searchParams.set('product', this.dataset.productHandle);
    if (this.dataset.variantId) url.searchParams.set('variant', this.dataset.variantId);

    const response = await fetch(url, { signal, headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`serviceability ${response.status}`);

    const data = await response.json();
    if (typeof data?.serviceable !== 'boolean') throw new Error('serviceability: malformed response');
    return data;
  }

  /* Deterministic, list-driven, and flagged. Not a delivery answer - a way to
     see the four states without a backend. */
  #fixtureAnswer(pincode) {
    const prefixes = (this.dataset.fixtureServiceable || '')
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    const serviceable = prefixes.some((p) => pincode.startsWith(p));
    return {
      serviceable,
      pincode,
      eta: serviceable ? this.dataset.fixtureEta || '' : '',
      fixture: true,
    };
  }

  #fill(template, pincode) {
    return String(template || '').replace('[pincode]', pincode);
  }

  #clear() {
    this.#lastChecked = null;
    this.#result.hidden = true;
    if (this.#main) this.#main.textContent = '';
    if (this.#eta) { this.#eta.textContent = ''; this.#eta.hidden = true; }
    this.removeAttribute('data-state');
    if (this.#hint) this.#hint.hidden = false;
  }

  #render(state, message, isFixture, eta) {
    this.setAttribute('data-state', state);
    this.#result.hidden = false;
    if (this.#main) this.#main.textContent = message || '';
    if (this.#eta) {
      this.#eta.textContent = eta || '';
      this.#eta.hidden = !eta;
    }
    if (isFixture) this.#result.setAttribute('data-fixture', '');
    else this.#result.removeAttribute('data-fixture');
    // The hint explains how to use the field; once there is a result it is noise.
    if (this.#hint) this.#hint.hidden = state !== 'invalid';
  }
}

if (!customElements.get('hc-pincode')) {
  customElements.define('hc-pincode', HcPincode);
}
